import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PawPrint, Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import JsonLd from "../components/JsonLd";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bullet copy for the value-prop column. Keep these short — single-line
// elevator hits, not paragraphs. Hosts skim this page on phones.
const PERKS = [
  {
    emoji: "💸",
    title: "Earn extra income",
    body: "Set your own hourly rate — keep what dog parents pay, minus a flat booking fee.",
  },
  {
    emoji: "🚪",
    title: "No membership friction",
    body: "Guests don't pay a monthly fee, so you get more bookings than gated marketplaces.",
  },
  {
    emoji: "⭐",
    title: "Founding host rate",
    body: "First 25 hosts lock in a permanently lower fee — even when we raise rates.",
  },
  {
    emoji: "🛡️",
    title: "You stay in control",
    body: "Set rules, approve guests, choose your own hours. We just send you the booking.",
  },
];

const PrivateParkHost = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    yard_description: "",
  });
  const [step, setStep] = useState("form"); // 'form' | 'submitting' | 'done'

  const isValid =
    form.name.trim().length >= 2 && /^.+@.+\..+$/.test(form.email.trim());

  useEffect(() => {
    document.title = "List Your Yard on Pawground — Host Waitlist";
    const setMeta = (sel, attr, value) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, value);
    };
    const desc =
      "Got a fenced yard? Earn extra income hosting dogs on Pawground. " +
      "No membership fees for guests, flat booking fee for hosts. Join the founding-host waitlist.";
    const canonical = "https://pawground.app/private-parks/host";
    setMeta('meta[name="description"]', "content", desc);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[property="og:title"]', "content", "List Your Yard on Pawground");
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[name="twitter:title"]', "content", "List Your Yard on Pawground");
    setMeta('meta[name="twitter:description"]', "content", desc);
    // Indexable — this is a public landing page.
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute("content", "index, follow");
  }, []);

  const submit = async () => {
    if (!isValid) return;
    setStep("submitting");
    try {
      const res = await fetch(`${API}/private-park-host-applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Couldn't submit your application");
      }
      setStep("done");
    } catch (e) {
      setStep("form");
      toast.error(e.message || "Couldn't submit — please try again.");
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "List Your Yard on Pawground — Host Waitlist",
    url: "https://pawground.app/private-parks/host",
    description:
      "Apply to become a Pawground private park host. Earn extra income, lock in a founding-host rate.",
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2d2a26]">
      <JsonLd data={jsonLd} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-16">
        {/* Hero */}
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-semibold uppercase tracking-wider">
            <PawPrint className="w-3.5 h-3.5" /> Host Waitlist
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-[1.05]">
            List Your Yard on Pawground.
          </h1>
          <p className="text-lg sm:text-xl text-[#4a4640] mt-5 leading-relaxed">
            Got a fenced yard?{" "}
            <span className="font-semibold text-[#2d2a26]">Earn extra income</span> by
            hosting dogs — no membership fees for guests, flat booking fee for hosts,
            and founding hosts lock in a permanently lower rate.
          </p>
        </div>

        {/* Body: perks + form */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8 lg:gap-12">
          {/* Perks */}
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PERKS.map((p) => (
                <div
                  key={p.title}
                  className="rounded-2xl bg-white border border-[#e5e0d8] p-4 flex gap-3"
                >
                  <span className="text-2xl shrink-0" aria-hidden>
                    {p.emoji}
                  </span>
                  <div>
                    <div className="font-semibold text-[#2d2a26]">{p.title}</div>
                    <div className="text-sm text-[#6b6560] mt-0.5 leading-relaxed">
                      {p.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* What we look for */}
            <div className="mt-8 rounded-2xl bg-[#4A7C59]/8 border border-[#4A7C59]/20 p-5">
              <h2 className="font-heading text-lg font-bold text-[#2d2a26] mb-3">
                What makes a great Pawground yard
              </h2>
              <ul className="space-y-2 text-[#4a4640] text-sm leading-relaxed">
                {[
                  "Fully fenced perimeter — no gaps a determined dog could slip through",
                  "Shade, water, and a clean ground surface (grass, mulch, turf — your call)",
                  "Pet-safe plants and no toxic chemicals on the lawn",
                  "Photos that actually show the yard (we'll help you stage if needed)",
                  "Reliable availability — even a few hours a week is a great start",
                ].map((t) => (
                  <li key={t} className="flex gap-2">
                    <Check className="w-4 h-4 text-[#4A7C59] mt-0.5 shrink-0" strokeWidth={3} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-sm text-[#6b6560] mt-6">
              Already exploring places to take your own dog?{" "}
              <Link to="/" className="text-[#4A7C59] font-semibold underline">
                Browse Pawground →
              </Link>
            </p>
          </div>

          {/* Form */}
          <aside>
            <div className="rounded-3xl bg-white border border-[#e5e0d8] shadow-[0_6px_30px_rgba(45,42,38,0.06)] p-5 sm:p-6 lg:sticky lg:top-24">
              {step === "done" ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-[#4A7C59]/15 text-[#4A7C59] flex items-center justify-center mx-auto mb-4">
                    <Check className="w-7 h-7" strokeWidth={3} />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#2d2a26] mb-2">
                    You're on the list!
                  </h2>
                  <p className="text-[#6b6560] leading-relaxed">
                    Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}. We'll
                    reach out within a few days to chat about your yard and walk you
                    through what comes next.
                  </p>
                  <Link
                    to="/"
                    data-testid="host-waitlist-done-home"
                    className="mt-6 inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[#2d2a26] text-white text-sm font-semibold hover:bg-black transition"
                  >
                    Back to Pawground <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                  }}
                  className="space-y-3"
                >
                  <h2 className="font-heading text-xl font-bold text-[#2d2a26]">
                    Join the host waitlist
                  </h2>
                  <p className="text-sm text-[#6b6560] -mt-2">
                    Takes ~60 seconds. We'll email you when we're ready to onboard your area.
                  </p>

                  <FormField label="Your name *">
                    <input
                      data-testid="host-waitlist-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      className={INPUT_CLS}
                      placeholder="First and last"
                    />
                  </FormField>

                  <FormField label="Email *">
                    <input
                      data-testid="host-waitlist-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      className={INPUT_CLS}
                      placeholder="you@example.com"
                    />
                  </FormField>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FormField label="Phone">
                      <input
                        data-testid="host-waitlist-phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className={INPUT_CLS}
                        placeholder="(555) 555-0123"
                      />
                    </FormField>
                    <FormField label="City">
                      <input
                        data-testid="host-waitlist-city"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        className={INPUT_CLS}
                        placeholder="St. Petersburg, FL"
                      />
                    </FormField>
                  </div>

                  <FormField label="Tell us about your yard">
                    <textarea
                      data-testid="host-waitlist-yard"
                      rows={4}
                      value={form.yard_description}
                      onChange={(e) => setForm({ ...form, yard_description: e.target.value })}
                      className={`${INPUT_CLS} resize-none`}
                      placeholder="Fence height + material, approximate size, shade, water, what makes it special for dogs…"
                    />
                  </FormField>

                  <button
                    type="submit"
                    data-testid="host-waitlist-submit"
                    disabled={!isValid || step === "submitting"}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#4A7C59] hover:bg-[#3d6749] disabled:bg-[#a8baaa] disabled:cursor-not-allowed text-white font-semibold transition"
                  >
                    {step === "submitting" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Join the waitlist
                  </button>
                  <p className="text-center text-xs text-[#6b6560]">
                    No commitment · No spam · We email once when your area is opening up.
                  </p>
                </form>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

const INPUT_CLS =
  "w-full px-3 py-2 rounded-xl border border-[#e5e0d8] bg-white focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/40 focus:border-[#4A7C59] text-sm";

const FormField = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold uppercase tracking-wider text-[#6b6560] mb-1.5">
      {label}
    </span>
    {children}
  </label>
);

export default PrivateParkHost;
